/**
 * Complexity Indicator — data helpers for building live criteria and
 * collecting source documents from findings.
 */

import type { Doc } from "@cvx/_generated/dataModel";
import type { CriterionDetail, FindingWithDocs } from "./types";

/**
 * `listIndicatorFindingsForJob` still declares `returns: v.array(v.any())`,
 * but its handler returns `indicatorFindings` rows unchanged — type the
 * boundary here as the stored document shape rather than editing the query.
 */
type IndicatorFinding = Doc<"indicatorFindings">;

const CONFIDENCE_RANK: Record<string, number> = { high: 3, medium: 2, low: 1 };

/**
 * Builds a criteria map from live DB findings (used while agent is running).
 * Picks the highest-confidence finding per criterion key.
 */
export function buildLiveCriteria(
  findings: IndicatorFinding[],
): Record<string, CriterionDetail> | undefined {
  if (findings.length === 0) return undefined;

  const best: Record<string, IndicatorFinding> = {};
  for (const f of findings) {
    const existing = best[f.criterionKey];
    if (
      !existing ||
      (CONFIDENCE_RANK[f.confidence] ?? 0) >
        (CONFIDENCE_RANK[existing.confidence] ?? 0)
    ) {
      best[f.criterionKey] = f;
    }
  }

  const build = (
    dbKey: string,
    outputKey: string,
  ): [string, CriterionDetail] => {
    const f = best[dbKey];
    if (!f)
      return [
        outputKey,
        {
          met: false,
          detail: "Waiting for evidence...",
          evidenceValue: null,
          confidence: "low",
        },
      ];
    return [
      outputKey,
      {
        met: f.met,
        detail: f.finding,
        evidenceValue: f.evidenceValue ?? null,
        confidence: f.confidence,
      },
    ];
  };

  return Object.fromEntries([
    build("company_assets", "companyAssets"),
    build("creditor_threshold", "creditorThreshold"),
    build("director_assets", "directorAssets"),
  ]);
}

/**
 * Groups findings by their criterionKey for source document lookup.
 */
export function groupFindingsByKey(
  findings: IndicatorFinding[],
): Record<string, FindingWithDocs[]> {
  const groups: Record<string, FindingWithDocs[]> = {};
  for (const f of findings) {
    if (!groups[f.criterionKey]) groups[f.criterionKey] = [];
    groups[f.criterionKey].push(f);
  }
  return groups;
}

/**
 * Builds a live secured-interests flag from DB findings (used while agent is running).
 */
export function buildLiveSecuredInterestsFlag(
  findings: IndicatorFinding[],
): { flagged: boolean; detail: string } | undefined {
  const securityFindings = findings.filter(
    (f) => f.criterionKey === "secured_interests",
  );
  if (securityFindings.length === 0) return undefined;

  const best = securityFindings.reduce((acc, f) =>
    (CONFIDENCE_RANK[f.confidence] ?? 0) >
    (CONFIDENCE_RANK[acc.confidence] ?? 0)
      ? f
      : acc,
  );
  return { flagged: best.met, detail: best.finding };
}

/**
 * Deduplicates and collects all source documents from a set of findings.
 */
export function collectSourceDocs(
  findings?: FindingWithDocs[],
): Array<{ documentId: string; fileName: string }> {
  if (!findings) return [];
  const seen = new Set<string>();
  const docs: Array<{ documentId: string; fileName: string }> = [];
  for (const f of findings) {
    for (const doc of f.sourceDocuments ?? []) {
      if (!seen.has(doc.documentId)) {
        seen.add(doc.documentId);
        docs.push(doc);
      }
    }
  }
  return docs;
}
